import type { SyncState } from './note'
import { normalizeMeasurementPreference, type MeasurementPreference } from './units'
import { createId } from '../utils/id'
import {
  normalizeReminderQuickOptions,
  type ReminderQuickOptions,
  type WeekStartsOn,
} from './reminder'

export const AUTO_EXPIRE_OPTIONS = [7, 30, 60, 90] as const
export type AutoExpireDays = (typeof AUTO_EXPIRE_OPTIONS)[number]
export type AppStartMode = 'capture' | 'browse'
export type UiSize = 'small' | 'medium' | 'large'
export type SettingsRecordType = 'global' | 'device'

interface SettingsBase {
  key: string
  schemaVersion: 1
  modifiedAt: number
  lastModifiedDeviceId: string
  revisionId: string
  localSyncState: SyncState
}

export interface GlobalSettings extends SettingsBase {
  key: 'global'
  recordType: 'global'
  autoExpireDays: AutoExpireDays
  reminderQuickOptions: ReminderQuickOptions
  weekStartsOn: WeekStartsOn
  /** Units for measurements the app shows, such as a cleaned recipe's quantities; "auto" follows the device's region. Absent means auto. */
  measurementSystem?: MeasurementPreference
}

export interface DeviceSettings extends SettingsBase {
  recordType: 'device'
  deviceId: string
  detectedName: string
  customName: string | null
  startMode: AppStartMode
  startModePreferenceVersion: 2
  uiSize: UiSize
  performanceDiagnosticsEnabled: boolean
  createdAt: number
  lastSeenAt: number
}

export type SettingsRecord = GlobalSettings | DeviceSettings

export const START_MODE_BOOTSTRAP_KEY = 'minimany:start-mode:v2'
export const UI_SIZE_BOOTSTRAP_KEY = 'minimany:ui-size:v1'
export const PERFORMANCE_DIAGNOSTICS_BOOTSTRAP_KEY = 'minimany:performance-diagnostics:v1'

type BootstrapStorage = Pick<Storage, 'getItem' | 'setItem'>

interface UserAgentDataLike {
  mobile: boolean
  platform: string
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>
}

export interface DeviceNameSignals {
  userAgent: string
  maxTouchPoints: number
  userAgentData?: UserAgentDataLike
}

function availableStorage(): BootstrapStorage | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage
}

export function isAutoExpireDays(value: unknown): value is AutoExpireDays {
  return typeof value === 'number' && AUTO_EXPIRE_OPTIONS.includes(value as AutoExpireDays)
}

export function isAppStartMode(value: unknown): value is AppStartMode {
  return value === 'capture' || value === 'browse'
}

export function isUiSize(value: unknown): value is UiSize {
  return value === 'small' || value === 'medium' || value === 'large'
}

export function isWeekStartsOn(value: unknown): value is WeekStartsOn {
  return value === 'monday' || value === 'sunday'
}

export function normalizeWeekStartsOn(value: unknown): WeekStartsOn {
  return isWeekStartsOn(value) ? value : 'monday'
}

export function defaultUiSizeForFormFactor(
  formFactor: 'desktop' | 'phone' | 'tablet',
): UiSize {
  if (formFactor === 'phone') return 'medium'
  return formFactor === 'desktop' ? 'small' : 'large'
}

export function readBootstrapStartMode(storage = availableStorage()): AppStartMode {
  if (!storage) return 'browse'
  try {
    const value = storage.getItem(START_MODE_BOOTSTRAP_KEY)
    return isAppStartMode(value) ? value : 'browse'
  } catch {
    return 'browse'
  }
}

export function writeBootstrapStartMode(mode: AppStartMode, storage = availableStorage()): void {
  if (!storage) return
  try {
    storage.setItem(START_MODE_BOOTSTRAP_KEY, mode)
  } catch {
    // IndexedDB remains authoritative when storage is unavailable.
  }
}

export function readBootstrapUiSize(
  fallback: UiSize,
  storage = availableStorage(),
): UiSize {
  if (!storage) return fallback
  try {
    const value = storage.getItem(UI_SIZE_BOOTSTRAP_KEY)
    return isUiSize(value) ? value : fallback
  } catch {
    return fallback
  }
}

export function writeBootstrapUiSize(size: UiSize, storage = availableStorage()): void {
  if (!storage) return
  try {
    storage.setItem(UI_SIZE_BOOTSTRAP_KEY, size)
  } catch {
    // IndexedDB remains authoritative when storage is unavailable.
  }
}

export function readBootstrapPerformanceDiagnostics(
  storage = availableStorage(),
): boolean {
  if (!storage) return false
  try {
    return storage.getItem(PERFORMANCE_DIAGNOSTICS_BOOTSTRAP_KEY) === 'enabled'
  } catch {
    return false
  }
}

export function writeBootstrapPerformanceDiagnostics(
  enabled: boolean,
  storage = availableStorage(),
): void {
  if (!storage) return
  try {
    storage.setItem(PERFORMANCE_DIAGNOSTICS_BOOTSTRAP_KEY, enabled ? 'enabled' : 'disabled')
  } catch {
    // IndexedDB remains authoritative when storage is unavailable.
  }
}

function normalizedModel(value: string | undefined): string | null {
  if (!value) return null
  const model = value
    .replace(/\s+Build\/.*$/i, '')
    .replace(/^wv\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
    .trim()
  if (
    model.length < 2 ||
    !/[a-z0-9].*[a-z0-9]/i.test(model) ||
    /^(?:android|android device|device|generic|k|mobile|phone|sdk|tablet|unknown|wv)$/i.test(model) ||
    /^[a-z]{2}(?:[-_][a-z]{2})?$/i.test(model) ||
    /(?:^|\s)(?:emulator|sdk built for)(?:\s|$)/i.test(model)
  ) return null
  return model
}

function androidModelFromUserAgent(userAgent: string): string | null {
  const section = [...userAgent.matchAll(/\(([^)]*)\)/g)]
    .map((match) => match[1] ?? '')
    .find((value) => /(?:^|;\s*)Android\s/i.test(value))
  if (!section) return null
  const parts = section.split(';').map((part) => part.trim())
  const androidIndex = parts.findIndex((part) => /^Android\s/i.test(part))
  if (androidIndex < 0) return null
  return parts
    .slice(androidIndex + 1)
    .map(normalizedModel)
    .filter((model): model is string => model !== null)
    .at(-1) ?? null
}

function automaticDeviceLabel(baseName: string, deviceId: string): string {
  const suffix = deviceId.replace(/[^a-z0-9]/gi, '').slice(0, 6).toUpperCase()
  return suffix ? `${baseName} · ${suffix}` : baseName
}

function currentDeviceNameSignals(): DeviceNameSignals {
  if (typeof navigator === 'undefined') return { userAgent: '', maxTouchPoints: 0 }
  const navigatorWithHints = navigator as Navigator & { userAgentData?: UserAgentDataLike }
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    userAgentData: navigatorWithHints.userAgentData,
  }
}

export function detectDefaultDeviceName(
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
  deviceId = '',
): string {
  const androidModel = androidModelFromUserAgent(userAgent)
  if (androidModel) return automaticDeviceLabel(androidModel, deviceId)

  let baseName = 'This device'
  if (/iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)) baseName = 'iPad'
  else if (/iPhone/i.test(userAgent)) baseName = 'iPhone'
  else if (/Android/i.test(userAgent)) baseName = /Mobile/i.test(userAgent) ? 'Android phone' : 'Android tablet'
  else if (/Windows/i.test(userAgent)) baseName = 'Windows PC'
  else if (/Macintosh|Mac OS X/i.test(userAgent)) baseName = 'Mac'
  else if (/CrOS/i.test(userAgent)) baseName = 'Chromebook'
  else if (/Linux/i.test(userAgent)) baseName = 'Linux device'
  return automaticDeviceLabel(baseName, deviceId)
}

export async function detectPreferredDeviceName(
  deviceId: string,
  signals = currentDeviceNameSignals(),
): Promise<string> {
  const fallback = detectDefaultDeviceName(signals.userAgent, signals.maxTouchPoints, deviceId)
  const userAgentData = signals.userAgentData
  if (!userAgentData?.getHighEntropyValues) return fallback
  try {
    const values = await userAgentData.getHighEntropyValues(['model'])
    const model = normalizedModel(values.model)
    if (model) return automaticDeviceLabel(model, deviceId)
    if (/^Android$/i.test(userAgentData.platform) && !/Android/i.test(signals.userAgent)) {
      return automaticDeviceLabel(userAgentData.mobile ? 'Android phone' : 'Android tablet', deviceId)
    }
  } catch {
    // Client hints are optional and may be withheld by browser privacy policy.
  }
  return fallback
}

export function normalizeCustomDeviceName(value: string, detectedName: string): string | null {
  const normalized = value.replace(/\s+/g, ' ').trim().slice(0, 64).trim()
  if (!normalized || normalized === detectedName) return null
  return normalized
}

export function displayDeviceName(settings: DeviceSettings): string {
  return settings.customName ?? settings.detectedName
}

export function createDefaultGlobalSettings(deviceId: string): GlobalSettings {
  return {
    key: 'global',
    recordType: 'global',
    schemaVersion: 1,
    autoExpireDays: 90,
    reminderQuickOptions: normalizeReminderQuickOptions(null),
    weekStartsOn: 'monday',
    measurementSystem: 'auto',
    modifiedAt: 0,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'local',
  }
}

export function updateReminderQuickOptions(
  current: GlobalSettings,
  reminderQuickOptions: ReminderQuickOptions,
  deviceId: string,
  now = Date.now(),
): GlobalSettings {
  return {
    ...current,
    reminderQuickOptions: normalizeReminderQuickOptions(reminderQuickOptions),
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function updateMeasurementSystem(
  current: GlobalSettings,
  measurementSystem: MeasurementPreference,
  deviceId: string,
  now = Date.now(),
): GlobalSettings {
  return {
    ...current,
    measurementSystem: normalizeMeasurementPreference(measurementSystem),
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function updateWeekStartsOn(
  current: GlobalSettings,
  weekStartsOn: WeekStartsOn,
  deviceId: string,
  now = Date.now(),
): GlobalSettings {
  return {
    ...current,
    weekStartsOn,
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function createDefaultDeviceSettings(
  deviceId: string,
  detectedName = detectDefaultDeviceName(undefined, undefined, deviceId),
  now = Date.now(),
  uiSize: UiSize = 'small',
): DeviceSettings {
  return {
    key: `device:${deviceId}`,
    recordType: 'device',
    schemaVersion: 1,
    deviceId,
    detectedName,
    customName: null,
    startMode: 'browse',
    startModePreferenceVersion: 2,
    uiSize,
    performanceDiagnosticsEnabled: false,
    createdAt: now,
    lastSeenAt: now,
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function updateGlobalSettings(
  current: GlobalSettings,
  autoExpireDays: AutoExpireDays,
  deviceId: string,
  now = Date.now(),
): GlobalSettings {
  return {
    ...current,
    autoExpireDays,
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function updateDeviceSettings(
  current: DeviceSettings,
  changes: Partial<Pick<DeviceSettings, 'customName' | 'detectedName' | 'lastSeenAt' | 'startMode' | 'startModePreferenceVersion' | 'uiSize' | 'performanceDiagnosticsEnabled'>>,
  deviceId: string,
  now = Date.now(),
): DeviceSettings {
  return {
    ...current,
    ...changes,
    modifiedAt: now,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function compareSettingsVersions(left: SettingsRecord, right: SettingsRecord): number {
  if (left.modifiedAt !== right.modifiedAt) return left.modifiedAt - right.modifiedAt
  const deviceOrder = left.lastModifiedDeviceId.localeCompare(right.lastModifiedDeviceId)
  if (deviceOrder !== 0) return deviceOrder
  return left.revisionId.localeCompare(right.revisionId)
}

export function settingEntityType(record: SettingsRecord): 'global-settings' | 'device-settings' {
  return record.recordType === 'global' ? 'global-settings' : 'device-settings'
}

export function normalizeSettingsRecord(value: unknown): SettingsRecord | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Partial<SettingsRecord>
  if (
    record.schemaVersion !== 1 ||
    typeof record.modifiedAt !== 'number' ||
    typeof record.lastModifiedDeviceId !== 'string' ||
    typeof record.revisionId !== 'string'
  ) return null

  if (record.recordType === 'global' && record.key === 'global' && isAutoExpireDays(record.autoExpireDays)) {
    return {
      ...record,
      reminderQuickOptions: normalizeReminderQuickOptions(record.reminderQuickOptions),
      weekStartsOn: normalizeWeekStartsOn(record.weekStartsOn),
      measurementSystem: normalizeMeasurementPreference(record.measurementSystem),
      localSyncState: 'synced',
    } as GlobalSettings
  }
  if (
    record.recordType === 'device' &&
    typeof record.key === 'string' &&
    typeof record.deviceId === 'string' &&
    record.key === `device:${record.deviceId}` &&
    typeof record.detectedName === 'string' &&
    (record.customName === null || typeof record.customName === 'string') &&
    isAppStartMode(record.startMode) &&
    typeof record.createdAt === 'number' &&
    typeof record.lastSeenAt === 'number'
  ) {
    return {
      ...record,
      startMode: record.startModePreferenceVersion === 2 ? record.startMode : 'browse',
      startModePreferenceVersion: 2,
      uiSize: isUiSize(record.uiSize) ? record.uiSize : 'small',
      performanceDiagnosticsEnabled: record.performanceDiagnosticsEnabled === true,
      localSyncState: 'synced',
    } as DeviceSettings
  }
  return null
}
